import React, { useEffect, useState } from 'react';
import { Modal } from '@appkit4/react-components/modal';
import { Button } from '@appkit4/react-components/button';
import { Input } from '@appkit4/react-components/field';
import { Select } from '@appkit4/react-components';
import { Tag } from '@appkit4/react-components/tag';
import { Upload } from '@appkit4/react-components/upload';
import './CreateTemplateModal.scss';
import { usePostV1TemplatesDownloadExampleExportTemplate } from '../../../api/generated/endpoint';
import { DownloadExampleExportTemplateRequest } from '../../../api/generated/models';
import useToast from '../../../helpers/useToast';

interface CreateTemplateModalProps {
    visible: boolean;
    onClose: () => void;
    onConfirm: (templateName: string, documentTypes: any[], file: any) => void;
    documentTypes: any[];
    isLoadingDocTypes: boolean;
}

export const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
    visible,
    onClose,
    onConfirm,
    documentTypes,
    isLoadingDocTypes,
}) => {
    const [templateName, setTemplateName] = useState('');
    const [selectedDocTypes, setSelectedDocTypes] = useState<any[]>([]);
    const [availableDocTypes, setAvailableDocTypes] = useState<any[]>([]);
    const [uploadedFile, setUploadedFile] = useState<any>(null);
    const showToast = useToast();

    const downloadExampleTemplateMutation =
        usePostV1TemplatesDownloadExampleExportTemplate();

    React.useEffect(() => {
        if (documentTypes) {
            setAvailableDocTypes(
                documentTypes.map((doctype: any) => ({
                    label: doctype.documentTypeName,
                    value: doctype.documentTypeId,
                }))
            );
        }
    }, []);

    useEffect(() => {
        if (visible) {
            setTemplateName('');
            setSelectedDocTypes([]);
            setUploadedFile(null);
        }
    }, [visible, documentTypes]);

    const handleDocumentTypeSelect = (value: any) => {
        const selectedDoc = availableDocTypes.find(
            (doc) => doc.value === value
        );
        if (selectedDoc) {
            setSelectedDocTypes([...selectedDocTypes, selectedDoc]);
            setAvailableDocTypes(
                availableDocTypes.filter((doc) => doc.value !== value)
            );
        }
    };

    const handleRemoveDocType = (value: string) => {
        const docToRemove = selectedDocTypes.find((doc) => doc.value === value);
        if (docToRemove) {
            setSelectedDocTypes(
                selectedDocTypes.filter((doc) => doc.value !== value)
            );
            setAvailableDocTypes([...availableDocTypes, docToRemove]);
        }
    };

    const handleFileUpload = (file: File, fileList: FileList): void => {
        if (!file || fileList.length === 0) {
            setUploadedFile(null);
        } else {
            setUploadedFile(file);
        }
    };

    const handleDownloadExample = () => {
        const documentTypeIds =
            selectedDocTypes.length > 0
                ? selectedDocTypes.map((docType) => docType.value)
                : [];

        const requestData: DownloadExampleExportTemplateRequest = {
            documentTypeIds: documentTypeIds,
        };

        downloadExampleTemplateMutation.mutate(
            { data: requestData },
            {
                onSuccess: (response: any) => {
                    if (response && response.exampleExportTemplateSASUrl) {
                        const downloadLink = document.createElement('a');
                        downloadLink.href =
                            response.exampleExportTemplateSASUrl;
                        downloadLink.target = '_blank';
                        downloadLink.download = 'example_template.xlsx';
                        document.body.appendChild(downloadLink);
                        downloadLink.click();
                        document.body.removeChild(downloadLink);
                        setAvailableDocTypes(
                            documentTypes.map((doctype: any) => ({
                                label: doctype.documentTypeName,
                                value: doctype.documentTypeId,
                            }))
                        );

                        showToast(
                            'Success',
                            'Example template downloaded successfully',
                            'success'
                        );
                    } else {
                        showToast(
                            'Error',
                            'Failed to download example template. No URL returned.',
                            'error'
                        );
                    }
                },
                onError: (error) => {
                    console.error('Error downloading example template:', error);
                    showToast(
                        'Error',
                        'Failed to download example template. Please try again.',
                        'error'
                    );
                },
            }
        );
    };

    const handleConfirm = () => {
        onConfirm(templateName, selectedDocTypes, uploadedFile);
    };

    return (
        <Modal
            visible={visible}
            onCancel={onClose}
            title="Create export template"
            modalStyle={{ width: '33.75rem' }}
        >
            <div className="create-template-modal-content">
                <div className="form-group">
                    <label htmlFor="templateName">Template name</label>
                    <Input
                        id="templateName"
                        type="text"
                        placeholder="Enter template name"
                        value={templateName}
                        onChange={(value: string) => setTemplateName(value)}
                    />
                </div>

                <div className="form-group">
                    <label>Document types</label>
                    {isLoadingDocTypes ? (
                        <div>Loading document types...</div>
                    ) : (
                        <>
                            <Select
                                placeholder="Select document type"
                                onSelect={handleDocumentTypeSelect}
                                data={availableDocTypes}
                            />
                            <div className="selected-documents">
                                {selectedDocTypes.map((doc) => (
                                    <Tag
                                        key={doc.value}
                                        onClose={() =>
                                            handleRemoveDocType(doc.value)
                                        }
                                        className="document-tag"
                                    >
                                        {doc.label}
                                    </Tag>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {selectedDocTypes.length > 0 && (
                    <>
                        <div className="download-example-container">
                            <Button
                                className="download-example-button"
                                icon="icon-download-outline"
                                onClick={handleDownloadExample}
                                size="small"
                            >
                                Download example template
                            </Button>
                        </div>
                    </>
                )}
                <div className="form-group">
                    <Upload
                        onChange={handleFileUpload}
                        multiple={false}
                        autoUpload={false}
                        acceptFileType=".xlsx,.xls"
                        uploadInstruction="You can upload excel files (.xlsx, .xls). The max file size is 10mb."
                        uploadTitle="Upload your template file"
                        maxFileSize={10 * 1024 * 1024}
                        config={{
                            trigger: false,
                            type: 'inline',
                            size: true,
                        }}
                    />
                </div>

                <div className="modal-actions">
                    <Button kind="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        kind="secondary"
                        onClick={handleConfirm}
                        disabled={
                            !templateName ||
                            selectedDocTypes.length === 0 ||
                            !uploadedFile
                        }
                    >
                        Create
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default CreateTemplateModal;
